import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Repeat, Search, Sparkles, Loader2, Eye, Save, Send,
  FileText, Globe, ExternalLink, Clock, CheckCircle2, Tag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RewriteArticle() {
  const { t } = useLanguage();
  const [sites, setSites] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("rewrite");

  // Workflow state
  const [step, setStep] = useState("url"); // url | scraped | rewriting | done
  const [scraping, setScraping] = useState(false);
  const [rewriting, setRewriting] = useState(false);

  // Form
  const [url, setUrl] = useState("");
  const [scraped, setScraped] = useState(null);
  const [aiModel, setAiModel] = useState("gemini");
  const [language, setLanguage] = useState("pl");
  const [tone, setTone] = useState("professional");
  const [siteId, setSiteId] = useState("");
  const [autoSeo, setAutoSeo] = useState(true);
  const [saveAs, setSaveAs] = useState("draft");

  // Result
  const [result, setResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [sitesRes, histRes] = await Promise.all([
        axios.get(`${API}/sites`),
        axios.get(`${API}/rewrite/history`),
      ]);
      setSites(sitesRes.data);
      setHistory(histRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleScrape = async () => {
    if (!url.trim()) { toast.error(t("rewrite.enterUrl")); return; }
    setScraping(true);
    try {
      const res = await axios.post(`${API}/rewrite/scrape`, { url: url.trim() });
      setScraped(res.data);
      setStep("scraped");
      toast.success(`${t("rewrite.scraped")}: ${res.data.word_count} ${t("rewrite.words")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setScraping(false); }
  };

  const handleRewrite = async () => {
    if (!scraped) return;
    setRewriting(true);
    setStep("rewriting");
    try {
      const res = await axios.post(`${API}/rewrite/generate`, {
        title: scraped.title,
        content: scraped.content,
        language, tone, ai_model: aiModel,
        site_id: siteId || null,
        auto_seo: autoSeo,
        save_as: saveAs,
      });
      setResult(res.data);
      setStep("done");
      fetchData();
      toast.success(t("rewrite.rewriteComplete"));
    } catch (err) {
      setStep("scraped");
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setRewriting(false); }
  };

  const resetAll = () => {
    setStep("url");
    setUrl("");
    setScraped(null);
    setResult(null);
  };

  return (
    <div data-testid="rewrite-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("rewrite.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("rewrite.subtitle")}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="rewrite-tabs">
          <TabsTrigger value="rewrite" className="gap-1.5">
            <Repeat className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("rewrite.rewriteTab")}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("rewrite.historyTab")}
            {history.length > 0 && <Badge variant="secondary" className="text-[10px] rounded-md ml-1">{history.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ===== REWRITE TAB ===== */}
        <TabsContent value="rewrite" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Lewa - kontrolki */}
            <div className="lg:col-span-2 space-y-4">
              {/* Krok 1: URL */}
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    <Search className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    {t("rewrite.sourceUrl")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1 space-y-3">
                  <div className="flex gap-2">
                    <Input data-testid="rewrite-url-input" value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://example.com/artykul" className="flex-1" />
                    <Button data-testid="scrape-btn" onClick={handleScrape}
                      disabled={scraping || !url.trim()} className="gap-1 shrink-0">
                      {scraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                       <Search className="w-3.5 h-3.5" strokeWidth={1.5} />}
                      {t("rewrite.scrape")}
                    </Button>
                  </div>
                  {scraped && (
                    <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 text-xs">
                      <p className="font-semibold text-emerald-700 dark:text-emerald-400">{scraped.title}</p>
                      <p className="text-emerald-600 dark:text-emerald-500 mt-1">{scraped.word_count} {t("rewrite.words")} {t("rewrite.scraped").toLowerCase()}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Krok 2: Opcje */}
              {(step === "scraped" || step === "rewriting" || step === "done") && (
                <Card className="rounded-xl border shadow-sm bg-card">
                  <CardHeader className="pb-2 pt-4 px-5">
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                      {t("rewrite.options")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-4 pt-1 space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("rewrite.aiModel")}</Label>
                      <Tabs value={aiModel} onValueChange={setAiModel}>
                        <TabsList className="grid grid-cols-3 w-full" data-testid="rewrite-model-tabs">
                          <TabsTrigger value="gpt">GPT</TabsTrigger>
                          <TabsTrigger value="claude">Claude</TabsTrigger>
                          <TabsTrigger value="gemini">Gemini</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("rewrite.language")}</Label>
                        <Select value={language} onValueChange={setLanguage}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pl" className="text-xs">Polski</SelectItem>
                            <SelectItem value="en" className="text-xs">English</SelectItem>
                            <SelectItem value="de" className="text-xs">Deutsch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("rewrite.tone")}</Label>
                        <Select value={tone} onValueChange={setTone}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="professional" className="text-xs">{t("rewrite.tonePro")}</SelectItem>
                            <SelectItem value="casual" className="text-xs">{t("rewrite.toneCasual")}</SelectItem>
                            <SelectItem value="seo" className="text-xs">{t("rewrite.toneSeo")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {sites.length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("rewrite.targetSite")}</Label>
                        <Select value={siteId} onValueChange={setSiteId}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="" className="text-xs">—</SelectItem>
                            {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{t("rewrite.autoSeo")}</Label>
                      <Switch data-testid="auto-seo-switch" checked={autoSeo} onCheckedChange={setAutoSeo} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("rewrite.saveAs")}</Label>
                      <Select value={saveAs} onValueChange={setSaveAs}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft" className="text-xs">{t("rewrite.asDraft")}</SelectItem>
                          <SelectItem value="pending" className="text-xs">{t("rewrite.asPending")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Separator />
                    <Button data-testid="rewrite-btn" onClick={handleRewrite}
                      disabled={rewriting || step === "done"} className="w-full h-10 gap-2 rounded-lg">
                      {rewriting ? <Loader2 className="w-4 h-4 animate-spin" /> :
                       <Repeat className="w-4 h-4" strokeWidth={1.5} />}
                      {rewriting ? t("rewrite.rewriting") : t("rewrite.rewriteNow")}
                    </Button>
                    {step === "done" && (
                      <Button variant="outline" onClick={resetAll} className="w-full h-8 text-xs gap-1 rounded-lg">
                        {t("rewrite.newRewrite")}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Prawa - podglad */}
            <div className="lg:col-span-3">
              <Card className="rounded-xl border shadow-sm bg-card h-full">
                <CardHeader className="pb-2 pt-4 px-5">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <Eye className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      {step === "done" ? t("rewrite.rewrittenPreview") : t("rewrite.originalPreview")}
                    </CardTitle>
                    {result && (
                      <div className="flex items-center gap-1.5">
                        <Badge variant="secondary" className="text-[10px] rounded-md">{result.word_count} {t("rewrite.words")}</Badge>
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] border-0 rounded-md">
                          <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> {t("rewrite.done")}
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-5 pt-1">
                  {!scraped && !result ? (
                    <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                      <Repeat className="w-12 h-12 mb-4 opacity-15" strokeWidth={1} />
                      <p className="text-xs">{t("rewrite.pasteUrlHint")}</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[450px] pr-3">
                      {/* Wynik przepisania */}
                      {result ? (
                        <div className="space-y-4">
                          <h2 className="font-heading text-lg font-bold text-foreground">{result.title}</h2>
                          <div className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap font-body">
                            {result.content}
                          </div>

                          {/* SEO Meta */}
                          {result.seo_meta && (
                            <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">SEO Meta</p>
                              <div className="text-xs space-y-1">
                                <p><span className="font-medium text-muted-foreground">Title:</span> {result.seo_meta.meta_title}</p>
                                <p><span className="font-medium text-muted-foreground">Desc:</span> {result.seo_meta.meta_description}</p>
                                {result.seo_meta.keywords?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {result.seo_meta.keywords.map((kw, i) => (
                                      <Badge key={i} variant="outline" className="text-[9px] rounded-md">{kw}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : scraped ? (
                        /* Oryginal */
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <a href={scraped.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-0.5">
                              {scraped.url.substring(0, 60)}... <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          </div>
                          <h2 className="font-heading text-lg font-bold text-foreground">{scraped.title}</h2>
                          {scraped.description && (
                            <p className="text-xs text-muted-foreground italic">{scraped.description}</p>
                          )}
                          <Separator />
                          <div className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap font-body">
                            {scraped.content.substring(0, 3000)}
                            {scraped.content.length > 3000 && "\n\n..."}
                          </div>
                        </div>
                      ) : null}
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ===== HISTORY TAB ===== */}
        <TabsContent value="history" className="mt-4">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {history.length === 0 ? (
                <div data-testid="no-history" className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Repeat className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("rewrite.noHistory")}</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[500px]">
                  <div className="divide-y divide-border">
                    {history.map((post) => (
                      <div key={post.id} data-testid={`rewrite-history-${post.id}`}
                        className="flex items-start gap-3 p-4">
                        <Repeat className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{post.title}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <Badge variant="secondary" className="text-[9px] rounded-md">{post.ai_model?.toUpperCase()}</Badge>
                            <Badge variant="outline" className="text-[9px] rounded-md">{post.word_count} {t("rewrite.words")}</Badge>
                            <span className="text-[9px] text-muted-foreground">
                              {new Date(post.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
